import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../Button';
import Badge from '../Badge';
import TestProgress from './TestProgress';
import TestResults from './TestResults';
import FreeTrialLimitModal from '../auth/FreeTrialLimitModal';
import styles from '../../styles/test/TestStyles.module.css';

/**
 * TestLayout component for consistent test page layout
 * Handles loading, error, completion, and active test states
 */
const TestLayout = ({
  title,
  type,
  testStatus,
  cargando,
  error,
  testCompletado,
  resultado,
  noQuestionsFound,
  preguntaActual,
  totalQuestions,
  children,
  hasExplanation,
  isLastQuestion
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!testStatus?.type === 'registered';
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showTrialLimitModal, setShowTrialLimitModal] = useState(false);
  
  // Check if current route is the random test route
  const isRandomTestRoute = location.pathname === '/test-aleatorio';

  if (cargando) {
    return (
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <h2>Cargando preguntas...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Check if this is an authentication error related to trial limits
    if (error.type === 'AUTH' || error.type === 'access') {
      // Extract the next test time from the error message if available
      let nextTestTime = null;
      
      // Try to extract the time from the error message
      const timeMatch = error.message && error.message.match(/(\d+h \d+m)/);
      if (timeMatch && timeMatch[1]) {
        // Calculate the next test time based on the extracted hours and minutes
        const [hours, minutes] = timeMatch[1].split(' ');
        const hoursValue = parseInt(hours.replace('h', ''));
        const minutesValue = parseInt(minutes.replace('m', ''));
        
        const nextTest = new Date();
        nextTest.setHours(nextTest.getHours() + hoursValue);
        nextTest.setMinutes(nextTest.getMinutes() + minutesValue);
        nextTestTime = nextTest.toISOString();
      }
      
      // Show only the FreeTrialLimitModal instead of the error message
      return (
        <FreeTrialLimitModal
          isOpen={true}
          onClose={() => navigate('/')}
          initialStatus={error.trialStatus || {
            nextTestTime: error.nextTestTime || nextTestTime,
            ipLimited: error.ipLimited || (error.message && error.message.includes('IP')),
            sessionLimited: error.sessionLimited
          }}
        />
      );
    }
    
    // For other errors, show the standard error message
    return (
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <h2>{error.type === 'access' ? 'Acceso No Disponible' : 'Error al cargar las preguntas'}</h2>
            <p>{error.message}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
              <Button
                variant="primary"
                onClick={() => navigate('/')}
              >
                Volver al inicio
              </Button>
              {!isAuthenticated && (
                <Button
                  variant="secondary"
                  onClick={() => setShowTokenModal(true)}
                >
                  Acceso Premium
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (testCompletado && resultado) {
    return (
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <h2 style={{ margin: '1rem 0', fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
              Test Completado
            </h2>
            <TestResults
              resultado={resultado}
              isFreeTest={testStatus?.type === 'trial'}
            />
          </div>
        </div>
      </div>
    );
  }

  if (noQuestionsFound) {
    return (
      <div className={styles.testPageContainer}>
        <div className={styles.testContent}>
          <div className={styles.container}>
            <h2 style={{ margin: '1rem 0', fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
              Error al cargar las preguntas
            </h2>
            <p>No se encontraron preguntas para el test</p>
            <Button
              variant="primary"
              onClick={() => navigate('/')}
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main test content
  return (
    <div className={styles.testPageContainer}>
      <div className={`${styles.testContent} ${hasExplanation ? styles.hasExplanation : ''} ${isLastQuestion ? styles.hasThreeButtons : ''}`}>
        <div className={styles.container}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '1rem', textAlign: 'center' }}>
            <h2 className={styles.testTitle} style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              {title}
            </h2>
            {/* Only show the badge for free trial users, not for authenticated users */}
            {testStatus?.type !== 'registered' && <Badge type="free" />}
          </div>
          
          <TestProgress current={preguntaActual + 1} total={totalQuestions} />
          
          {children}
        </div>
      </div>
    </div>
  );
};

TestLayout.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  testStatus: PropTypes.object,
  cargando: PropTypes.bool.isRequired,
  error: PropTypes.object,
  testCompletado: PropTypes.bool.isRequired,
  resultado: PropTypes.object,
  noQuestionsFound: PropTypes.bool,
  preguntaActual: PropTypes.number,
  totalQuestions: PropTypes.number,
  children: PropTypes.node,
  hasExplanation: PropTypes.bool,
  isLastQuestion: PropTypes.bool
};

export default TestLayout;